import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext.js';
import { photoService } from '../services/photoService.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { analyticsEvents } from '../config/analyticsConfig.js';
import ConfirmModal from './ConfirmModal.js';
import placeholderImage from '../images/placeholder.png';
import './Profile.css';

function Profile() {
  usePageTitle('Perfil');
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(user?.image || null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [formData, setFormData] = useState({
    id: user?.id || null,
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    image: user?.image || null,
    provider: user?.provider || 'local'
  });
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Rastrear visualização do perfil
    analyticsEvents.viewProfile();
  }, []);

  useEffect(() => {
    // Atualizar formData quando user mudar
    if (user) {
      setFormData({
        id: user?.id || null,
        name: user?.name || '',
        email: user?.email || '',
        phone: formatPhoneForDisplay(user?.phone || ''),
        image: user?.image || null,
        provider: user?.provider || 'local'
      });
      setAvatarUrl(user?.image || null);
    }
  }, [user]);

  const formatPhoneForDisplay = (phone) => {
    if (!phone) return '';
    
    // Remove tudo exceto números
    let numbers = phone.replace(/\D/g, '');
    
    // Se não começar com 55, adiciona
    if (!numbers.startsWith('55') && numbers.length > 0) {
      numbers = '55' + numbers;
    }
    
    // Formata para exibição
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) return `+${numbers}`;
    if (numbers.length <= 4) return `+${numbers.substring(0, 2)} (${numbers.substring(2)}`;
    if (numbers.length <= 9) return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4)}`;
    if (numbers.length === 13) {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 9)}-${numbers.substring(9)}`;
    } else if (numbers.length >= 10) {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 8)}-${numbers.substring(8)}`;
    }
    return `+${numbers}`;
  };

  const removePhoneMask = (phone) => {
    if (!phone) return '';
    // Remove tudo exceto números
    const numbers = phone.replace(/\D/g, '');
    // Retorna com +55
    if (numbers.startsWith('55')) {
      return `+${numbers}`;
    }
    return `+55${numbers}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Se for o campo de telefone, formatar
    if (name === 'phone') {
      const formatted = formatPhoneInput(value);
      setFormData(prev => ({
        ...prev,
        [name]: formatted
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const formatPhoneInput = (value) => {
    // Remove tudo exceto números
    let numbers = value.replace(/\D/g, '');
    
    // Se não começar com 55, adiciona
    if (!numbers.startsWith('55') && numbers.length > 0) {
      numbers = '55' + numbers;
    }
    
    // Limita a 13 dígitos (55 + 11 dígitos)
    numbers = numbers.substring(0, 13);
    
    // Formata: +55 (XX) XXXXX-XXXX ou +55 (XX) XXXX-XXXX
    if (numbers.length === 0) return '';
    if (numbers.length <= 2) return `+${numbers}`;
    if (numbers.length <= 4) return `+${numbers.substring(0, 2)} (${numbers.substring(2)}`;
    if (numbers.length <= 9) return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4)}`;
    if (numbers.length <= 10) return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 8)}-${numbers.substring(8)}`;
    // Para celular (11 dígitos) ou fixo (10 dígitos)
    if (numbers.length === 13) {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 9)}-${numbers.substring(9)}`;
    } else {
      return `+${numbers.substring(0, 2)} (${numbers.substring(2, 4)}) ${numbers.substring(4, 8)}-${numbers.substring(8)}`;
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Preparar dados para enviar (incluindo a imagem atualizada)
      const dataToUpdate = {
        id: formData.id,
        name: formData.name,
        email: formData.email,
        phone: removePhoneMask(formData.phone), // Remove máscara antes de enviar
        image: avatarUrl || formData.image
      };
      
      // Chamar o método updateUser do contexto
      const result = await updateUser(dataToUpdate);
      
      if (result.success) {
        analyticsEvents.updateProfile();
        setEditing(false);
        toast.success('Perfil atualizado com sucesso!');
      } else {
        toast.error(result.message || 'Erro ao atualizar perfil');
      }
    } catch (err) {
      console.error('Erro ao salvar perfil:', err);
      toast.error('Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      id: user?.id || null,
      name: user?.name || '',
      email: user?.email || '',
      phone: formatPhoneForDisplay(user?.phone || ''),
      image: user?.image || null,
      provider: user?.provider || 'local'
    });
    setAvatarUrl(user?.image || null);
    setEditing(false);
  };

  const handleAvatarClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarChange = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    setUploadingAvatar(true);

    try {
      const file = files[0];
      
      // Upload para Firebase Storage no caminho /profile
      const uploadResult = await photoService.uploadProfileAvatar(file, user.id);
      
      if (uploadResult.success) {
        const imageUrl = uploadResult.url;
        setAvatarUrl(imageUrl);
        
        // Atualizar também o formData para ser enviado no save
        setFormData(prev => ({
          ...prev,
          image: imageUrl
        }));
        
        console.log('✅ Avatar uploaded to Firebase:', imageUrl);
        toast.success('Imagem carregada! Clique em "Salvar" para aplicar as alterações.', {
          duration: 5000,
        });
      } else {
        toast.error(uploadResult.error || 'Erro ao carregar imagem');
      }
    } catch (err) {
      console.error('Erro ao fazer upload do avatar:', err);
      toast.error('Erro ao enviar foto');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getAvatarUrl = () => {
    return avatarUrl;
  };

  const getInitials = () => {
    if (!formData.name) return '?';
    const names = formData.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }
    return formData.name.substring(0, 2).toUpperCase();
  };

  const handleDeleteAccount = () => {
    setShowDeleteModal(true);
  };

  const confirmDeleteAccount = () => {
    window.location.href = 'https://vetsse.com.br/support';
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="spinner"></div>
          <p>Carregando perfil...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDeleteAccount}
        title="Deletar Conta"
        message="Tem certeza que deseja deletar sua conta? Esta ação não pode ser desfeita."
        confirmText="Confirmar Exclusão"
        type="danger"
      />
      <div className="profile-container">
      <div className="profile-header">
        <button className="profile-back-btn" onClick={() => navigate('/dashboard')}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          Voltar
        </button>
        <h1>Meu Perfil</h1>
        <div className="profile-header-actions">
          {!editing ? (
            <button className="profile-edit-btn" onClick={() => setEditing(true)}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Editar
            </button>
          ) : (
            <>
              <button className="profile-cancel-btn" onClick={handleCancel}>
                Cancelar
              </button>
              <button className="profile-save-btn" onClick={handleSave}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Salvar
              </button>
            </>
          )}
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-card profile-avatar-section">
          <div className="profile-avatar-wrapper">
            {getAvatarUrl() && (
              <div className={`profile-avatar ${uploadingAvatar ? 'uploading' : ''}`}>
                <img 
                  src={getAvatarUrl()} 
                  alt="Avatar" 
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = placeholderImage;
                  }}
                />
                {uploadingAvatar && (
                  <div className="profile-avatar-loading">
                    <div className="spinner"></div>
                  </div>
                )}
              </div>
            )}
            {editing && (
              <button 
                className="profile-avatar-change-btn" 
                onClick={handleAvatarClick}
                disabled={uploadingAvatar}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                  <path d="M23 19C23 19.5304 22.7893 20.0391 22.4142 20.4142C22.0391 20.7893 21.5304 21 21 21H3C2.46957 21 1.96086 20.7893 1.58579 20.4142C1.21071 20.0391 1 19.5304 1 19V8C1 7.46957 1.21071 6.96086 1.58579 6.58579C1.96086 6.21071 2.46957 6 3 6H7L9 3H15L17 6H21C21.5304 6 22.0391 6.21071 22.4142 6.58579C22.7893 6.96086 23 7.46957 23 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 17C14.2091 17 16 15.2091 16 13C16 10.7909 14.2091 9 12 9C9.79086 9 8 10.7909 8 13C8 15.2091 9.79086 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Alterar Foto
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleAvatarChange}
            />
          </div>
          <div className="profile-avatar-info">
            <h2>{formData.name || 'Sem nome'}</h2>
            <p className="profile-email">{formData.email}</p>
          </div>
        </div>

        <div className="profile-card">
          <h3 className="profile-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Informações Pessoais
          </h3>
          <div className="profile-form">
            <div className="profile-form-group">
              <label>Nome Completo</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!editing}
                placeholder="Digite seu nome"
              />
            </div>

            <div className="profile-form-group">
              <label>Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                disabled={true}
                placeholder="seu@email.com"
              />
              <small className="profile-form-hint">O email não pode ser alterado</small>
            </div>

            <div className="profile-form-group">
              <label>Telefone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                disabled={!editing}
                placeholder="+55 (00) 00000-0000"
              />
            </div>
          </div>
        </div>

        {/* <div className="profile-card">
          <h3 className="profile-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 11V7C7 5.67392 7.52678 4.40215 8.46447 3.46447C9.40215 2.52678 10.6739 2 12 2C13.3261 2 14.5979 2.52678 15.5355 3.46447C16.4732 4.40215 17 5.67392 17 7V11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Segurança
          </h3>
          <div className="profile-security-section">
            <div className="profile-security-item">
              <div className="profile-security-info">
                <h4>Senha</h4>
                <p>Última alteração há 30 dias</p>
              </div>
              <button className="profile-secondary-btn" disabled={editing}>
                Alterar Senha
              </button>
            </div>
            <div className="profile-security-item">
              <div className="profile-security-info">
                <h4>Autenticação em Dois Fatores</h4>
                <p>Adicione uma camada extra de segurança</p>
              </div>
              <button className="profile-secondary-btn" disabled={editing}>
                Configurar
              </button>
            </div>
          </div>
        </div> */}

        <div className="profile-card">
          <h3 className="profile-section-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Ajuda e Suporte
          </h3>
          <div className="profile-support-section">
            <div className="profile-support-item">
              <div className="profile-support-info">
                <h4>Central de Ajuda</h4>
                <p>Precisa de ajuda? Entre em contato com nosso suporte</p>
              </div>
              <button 
                className="profile-support-btn" 
                onClick={() => window.open('https://vetsse.com.br/support', '_blank')}
                disabled={editing}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                  <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                Acessar Suporte
              </button>
            </div>
          </div>
        </div>

        <div className="profile-card profile-danger-zone">
          <div className="profile-danger-content">
            <div className="profile-danger-item">
              <div>
                <h4>Sair da Conta</h4>
                <p>Você pode fazer login novamente a qualquer momento</p>
              </div>
              <button 
                className="profile-logout-btn" 
                onClick={logout}
                disabled={editing}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
                Sair
              </button>
            </div>
            <div className="profile-danger-item">
              <div>
                <h4>Excluir Conta</h4>
                <p>Esta ação não pode ser desfeita</p>
              </div>
              <button 
                className="profile-danger-btn" 
                onClick={handleDeleteAccount}
                disabled={editing}
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default Profile;
